// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import ApplicationCommandsStore from '@app/features/devtools/state/ApplicationCommandsStore';
import {runContextCommand} from '@app/features/devtools/utils/ContextCommandUtils';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {SubMenu as MenuItemSubmenu} from '@app/features/ui/action_menu/ContextMenu';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {pushApiErrorModal} from '@app/lib/forms';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const APPS_DESCRIPTOR = msg({
	message: 'Apps',
	context: 'apps-context-menu',
	comment: 'Context menu submenu that lists the commands bots offer for the selected user or message.',
});

export const USER_COMMAND_TYPE = 2;
export const MESSAGE_COMMAND_TYPE = 3;

interface AppsContextMenuGroupProps {
	commandType: typeof USER_COMMAND_TYPE | typeof MESSAGE_COMMAND_TYPE;
	targetId: string;
	onClose: () => void;
	guildId?: string | null;
	channelId?: string | null;
}

export const AppsContextMenuGroup: React.FC<AppsContextMenuGroupProps> = observer(
	({commandType, targetId, onClose, guildId: providedGuildId, channelId: providedChannelId}) => {
		const {i18n} = useLingui();
		const channelId = providedChannelId ?? SelectedChannel.currentChannelId ?? null;
		const guildId = providedGuildId ?? (channelId ? Channels.getChannel(channelId)?.guildId : null) ?? null;

		useEffect(() => {
			if (guildId) {
				void ApplicationCommandsStore.fetchCommandsForGuild(guildId);
			}
		}, [guildId]);

		if (!guildId || !channelId) return null;
		const commands = ApplicationCommandsStore.getContextCommands(guildId, commandType);
		if (commands.length === 0) return null;

		return (
			<MenuGroup data-flx="ui.action-menu.items.apps-context-menu-group.menu-group">
				<MenuItemSubmenu
					label={i18n._(APPS_DESCRIPTOR)}
					render={() => (
						<MenuGroup data-flx="ui.action-menu.items.apps-context-menu-group.commands-group">
							{commands.map((command) => (
								<MenuItem
									key={command.commandId}
									hint={command.botName}
									onClick={() => {
										onClose();
										runContextCommand({command, guildId, channelId, targetId}).catch((error) => {
											pushApiErrorModal(i18n, error);
										});
									}}
									data-flx="ui.action-menu.items.apps-context-menu-group.command-item"
								>
									{command.name}
								</MenuItem>
							))}
						</MenuGroup>
					)}
					data-flx="ui.action-menu.items.apps-context-menu-group.menu-item-submenu"
				/>
			</MenuGroup>
		);
	},
);
